
  import { mapState } from 'vuex';
  import { upperFirst } from 'lodash';
  import { defineComponent, h } from 'vue';
  import { MenuButton } from '@headlessui/vue';
  import { ChevronDownIcon, ExternalLinkIcon } from '@heroicons/vue/outline';
  import { AppDropdownMenu, AppAvatar, AppBadge } from '@/components';
  import { navigationItems } from './navigation-items';
  import MainMenuMobile from './MainMenu/Mobile.vue';
  import KennissLogo from '@/assets/KennissLogo.vue';
  import { mapGettersTyped } from '@/store/helpers';
  import RouterMixin from '@/mixins/router-mixin';
  import CompanyLogo from './CompanyLogo.vue';

  import type { DropdownItem } from '@/components/AppDropdownMenu';
  import type { DynamicDomClassesObject } from '@/typings/class-style-binding';
  import type { RouterPushReturnType } from '@/typings/vue-router.d';
  import type { RootGettersMapped } from '@/typings/vuex';
  import type { MenuItem } from '@/typings/user';
  import type { NavigationItem } from '.';

  export default defineComponent({
    name: 'PrimaryNav',

    components: {
      MainMenuMobile,
      AppAvatar,
      AppBadge,
      CompanyLogo,
      AppDropdownMenu,
      MenuButton,
      KennissLogo,
      ChevronDownIcon,
    },

    mixins: [
      RouterMixin,
    ],

    emits: [
      'logout',
    ],

    data() {
      return {
        open: false,
      };
    },

    computed: {
      ...mapState([
        'company',
        'user',
      ]),

      ...mapGettersTyped<RootGettersMapped>([
        'companyRole',
        'userHasAnyRole',
        'userHasPermission',
        'isGinkelCompany',
      ]),

      dynamicDomClasses(): DynamicDomClassesObject {
        const { showPrimaryNav } = this.$route.meta;
        const hideNavOnMobile = typeof showPrimaryNav === 'object'
          && !showPrimaryNav.mobile;

        return {
          '!hidden': showPrimaryNav === false || hideNavOnMobile,
          'md:!block': hideNavOnMobile,
        };
      },

      extraMenuItems(): DropdownItem[] {
        if (this.user?.menuItems?.length) {
          return this.user.menuItems.map(menuItem => ({
            name: `extra-menu-item-${menuItem.id}`,
            onSelectCallback: () => this.onMenuItemClick(menuItem),
            content: h('span', {
              class: 'inline-flex justify-center items-center gap-0.5',
            }, [
              menuItem.title,
              h(ExternalLinkIcon, {
                class: 'relative -top-0.5 h-3.5 w-3.5',
              }),
            ]),
          }));
        }

        return [];
      },

      userNavigation(): DropdownItem[] {
        const userNavigationItems: DropdownItem[] = [
          {
            name: 'profile',
            content: this.$t('navigation.profile'),
            permission: 'profile.preferences',
            onSelectCallback: async (): RouterPushReturnType => {
              return this.$router.push({ name: 'profile.preferences' });
            },
          },
          {
            name: 'userManagement',
            content: upperFirst(this.$tc('roles.administrator', 0)),
            permission: 'admin.users.list',
            onSelectCallback: async (): RouterPushReturnType => {
              return this.$router.push({ name: 'admin.users.list' });
            },
          },
          {
            name: 'logout',
            content: this.$t('logout'),
            onSelectCallback: (): void => this.$emit('logout'),
          },
        ];

        if (this.company?.canAddSubCompanies) {
          userNavigationItems.splice(1, 0, {
            name: 'createSubCompany',
            content: this.$t('navigation.createSubCompany'),
            permission: 'domain.companies.create',
            onSelectCallback: async (): RouterPushReturnType => {
              return this.$router.push({ name: 'companies.create' });
            },
          });
        }

        return userNavigationItems.filter(nav => {
          if (nav.permission) {
            return this.can(nav.permission);
          }

          return true;
        });
      },

      permittedItems(): NavigationItem[] {
        return navigationItems.map(navigationItem => {
          if (this.isGinkelCompany && navigationItem.name === 'navigation.openLibrary') {
            return { ...navigationItem, name: this.$t('knowledgeMeetings') };
          }

          return navigationItem;
        }).filter(navigationItem => {
          if (navigationItem.hideOnProduction && process.env.NODE_ENV === 'production') {
            return false;
          }

          if (navigationItem.roles) {
            if (this.userHasAnyRole(navigationItem.roles)) {
              return navigationItem.permission ? this.can(navigationItem.permission) : true;
            }

            return false;
          }

          return navigationItem.permission ? this.can(navigationItem.permission) : true;
        });
      },
    },

    methods: {
      onMenuItemClick(menuItem: MenuItem): void {
        window.open(menuItem.url, '_blank');
      },
    },
  });
